// =============================================================================
// Space Members Dialog - ClickUp-style sharing dialog
// Partage d'espace avec invitation par email + gestion des membres
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { inviteMemberToSpaceViaEdgeFunction } from '@/modules/project/data/spaceMembersRepository';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Users,
  Link2,
  Loader2,
  ChevronDown,
  Info,
  Lock,
  Trash2,
  Building2,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import type { PermissionLevel, SpaceMemberInfo, OrgMemberInfo } from '@/modules/project/data/permissionRepository';
import {
  fetchSpaceMembers,
  fetchOrganizationMembers,
  addSpaceMember,
  updateSpaceMemberPermission,
  removeSpaceMember,
} from '@/modules/project/data/permissionRepository';
import { PERMISSION_CONFIG, PERMISSION_LEVELS, PERMISSION_LEVEL_LABELS } from '@/lib/visibility-constants';
import type { Space } from '@/modules/project/data/spaceRepository';

interface SpaceMembersDialogProps {
  space: Space;
  orgId: string;
  open: boolean;
  onClose: () => void;
  canManage: boolean;
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

export function SpaceMembersDialog({
  space,
  orgId,
  open,
  onClose,
  canManage,
}: SpaceMembersDialogProps) {
  const { user } = useAuth();
  const [members, setMembers] = useState<SpaceMemberInfo[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMemberInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);

  const [inviteSearch, setInviteSearch] = useState('');
  const [defaultPermission, setDefaultPermission] = useState<PermissionLevel>('edit');
  const [isAdding, setIsAdding] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Effective canManage: org-level OR user has 'full' on this space
  const currentUserSpacePermission = useMemo(() => {
    if (!user) return null;
    const myMembership = members.find((m) => m.user_id === user.id);
    return myMembership?.permission || null;
  }, [members, user]);

  const effectiveCanManage = canManage || currentUserSpacePermission === 'full';

  // Load members
  const loadMembers = useCallback(async () => {
    if (!open || !space?.id) return;
    setLoading(true);
    try {
      const data = await fetchSpaceMembers(space.id);
      setMembers(data);
    } catch (err) {
      console.error('Error loading space members:', err);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  }, [open, space?.id]);

  const loadOrgMembers = useCallback(async () => {
    if (!orgId) return;
    setLoadingOrgMembers(true);
    try {
      const data = await fetchOrganizationMembers(orgId);
      setOrgMembers(data);
    } catch (err) {
      console.error('Error loading org members:', err);
    } finally {
      setLoadingOrgMembers(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (open) {
      loadMembers();
      loadOrgMembers();
    }
  }, [open, loadMembers, loadOrgMembers]);

  // Available members (not yet added) filtered by search
  const availableMembers = useMemo(() => {
    if (!inviteSearch.trim()) return [];
    return orgMembers
      .filter((om) => !members.some((m) => m.org_member_id === om.id))
      .filter(
        (om) =>
          (om.full_name?.toLowerCase() || '').includes(inviteSearch.toLowerCase()) ||
          om.email.toLowerCase().includes(inviteSearch.toLowerCase())
      );
  }, [orgMembers, members, inviteSearch]);

  // Check if input looks like an email
  const isEmailInput = inviteSearch.includes('@') && inviteSearch.includes('.');

  const handleAddMember = async (orgMemberId: string) => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      await addSpaceMember(space.id, orgMemberId, defaultPermission);
      await loadMembers();
      setInviteSearch('');
      setShowSuggestions(false);
      const addedMember = orgMembers.find((om) => om.id === orgMemberId);
      const memberName = addedMember?.full_name || addedMember?.email || 'Ce membre';
      toast.success(`${memberName} a maintenant accès à l'espace "${space.name}".`);
    } catch (err: any) {
      console.error('Error adding space member:', err);
      toast.error(err.message || "Erreur lors de l'ajout");
    } finally {
      setIsAdding(false);
    }
  };

  const handleInviteByEmail = async () => {
    if (!isEmailInput || isAdding) return;
    const emailToInvite = inviteSearch.trim().toLowerCase();

    // Check if this email matches an existing org member
    const matchingMember = orgMembers.find(
      (om) => om.email.toLowerCase() === emailToInvite
    );
    if (matchingMember) {
      if (members.some((m) => m.org_member_id === matchingMember.id)) {
        toast.error('Ce membre a déjà accès à cet espace');
        return;
      }
      await handleAddMember(matchingMember.id);
      return;
    }

    // External invitation via invite-member edge function
    setIsAdding(true);
    try {
      const result = await inviteMemberToSpaceViaEdgeFunction(
        orgId,
        emailToInvite,
        space.id,
        defaultPermission,
      );

      if (result?.success === false) {
        if (result.code === 'already_member') {
          toast.error('Cet utilisateur est déjà membre du workspace');
        } else if (result.code === 'pending_invitation') {
          toast.error('Une invitation est déjà en attente pour cet email');
        } else if (result.code === 'self_invite') {
          toast.error('Vous ne pouvez pas vous inviter vous-même');
        } else {
          toast.error(result.error || "Erreur lors de l'invitation");
        }
        return;
      }

      toast.success(`Invitation envoyée à ${emailToInvite}. Cette personne aura accès à l'espace "${space.name}" dès qu'elle acceptera.`);
      setInviteSearch('');
      setShowSuggestions(false);
    } catch (err: any) {
      console.error('Error inviting by email:', err);
      toast.error(err.message || "Erreur lors de l'envoi de l'invitation");
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdatePermission = async (orgMemberId: string, permission: PermissionLevel) => {
    try {
      await updateSpaceMemberPermission(space.id, orgMemberId, permission);
      await loadMembers();
      toast.success('Permission mise à jour');
    } catch (err: any) {
      console.error('Error updating permission:', err);
      toast.error(err.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleRemoveMember = async (orgMemberId: string) => {
    try {
      const result = await removeSpaceMember(space.id, orgMemberId);
      await loadMembers();
      if (result.wasLastExplicitFull) {
        toast.info(
          "Membre retiré. Seuls les propriétaires et admins du workspace ont désormais accès complet à cet espace.",
          { duration: 5000 }
        );
      } else {
        toast.success("Membre retiré de l'espace");
      }
    } catch (err: any) {
      console.error('Error removing member:', err);
      toast.error(err.message || 'Erreur lors de la suppression');
    }
  };

  const fullPermissionCount = members.filter((m) => m.permission === 'full').length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-base font-semibold">
            Partager cet espace
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
            Partage de l'espace avec toutes les vues
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-semibold text-white"
              style={{ backgroundColor: space.color || '#6366f1' }}
            >
              {space.name[0]?.toUpperCase()}
            </span>
            <span className="font-medium text-foreground">{space.name}</span>
          </p>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-4">
          {/* Invite input - ClickUp style */}
          {effectiveCanManage && (
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="Inviter par nom ou par e-mail"
                    value={inviteSearch}
                    onChange={(e) => {
                      setInviteSearch(e.target.value);
                      setShowSuggestions(e.target.value.trim().length > 0);
                    }}
                    onFocus={() => {
                      if (inviteSearch.trim().length > 0) setShowSuggestions(true);
                    }}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (availableMembers.length === 1) {
                          handleAddMember(availableMembers[0].id);
                        } else if (isEmailInput) {
                          handleInviteByEmail();
                        }
                      }
                    }}
                    className="h-9 text-sm"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    if (availableMembers.length === 1) {
                      handleAddMember(availableMembers[0].id);
                    } else if (isEmailInput) {
                      handleInviteByEmail();
                    }
                  }}
                  disabled={isAdding || (!isEmailInput && availableMembers.length === 0)}
                  className="h-9 px-4 text-sm font-semibold bg-foreground text-background hover:bg-foreground/90"
                >
                  {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Inviter'}
                </Button>
              </div>

              {/* Suggestions dropdown */}
              {showSuggestions && inviteSearch.trim().length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
                  {loadingOrgMembers ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : availableMembers.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground">
                      {isEmailInput
                        ? "Aucun membre trouvé — appuyez Entrée pour inviter par e-mail"
                        : 'Aucun membre disponible'}
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[180px]">
                      <div className="py-1">
                        {availableMembers.map((member) => (
                          <button
                            key={member.id}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 text-left transition-colors"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleAddMember(member.id)}
                            disabled={isAdding}
                          >
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={member.avatar_url || undefined} />
                              <AvatarFallback className="text-[10px] bg-muted">
                                {getInitials(member.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {member.full_name || 'Sans nom'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {member.email}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Link section */}
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-foreground">Lien privé</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  Partagez un lien d'accès direct à cet espace
                </TooltipContent>
              </Tooltip>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              disabled
            >
              Bientôt disponible
            </Button>
          </div>

          {/* Default permission */}
          {effectiveCanManage && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Permission par défaut</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Permission attribuée par défaut aux nouveaux membres
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select
                value={defaultPermission}
                onValueChange={(v) => setDefaultPermission(v as PermissionLevel)}
              >
                <SelectTrigger className="h-7 w-auto min-w-[140px] text-xs border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERMISSION_LEVELS.map((p) => (
                    <SelectItem key={p} value={p} className="text-xs">
                      {PERMISSION_LEVEL_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Shared with section */}
          <div className="border-t border-border pt-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2.5 font-medium">
              Partager avec
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="max-h-[240px]">
                <div className="space-y-0.5">
                  {/* Workspace row (always shown) */}
                  <div className="flex items-center justify-between py-2 px-1 rounded-md">
                    <div className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Workspace</p>
                        <p className="text-[11px] text-muted-foreground">
                          Environnement de travail
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Member avatars preview */}
                      <div className="flex -space-x-1.5">
                        {members.slice(0, 3).map((m) => (
                          <Avatar key={m.org_member_id} className="h-6 w-6 border-2 border-background">
                            <AvatarImage src={m.avatar_url || undefined} />
                            <AvatarFallback className="text-[8px] bg-muted">
                              {getInitials(m.full_name)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {members.length > 3 && (
                          <div className="h-6 w-6 rounded-full border-2 border-background bg-muted flex items-center justify-center">
                            <span className="text-[8px] text-muted-foreground font-medium">
                              +{members.length - 3}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Individual members */}
                  {members.map((member) => {
                    const perm = PERMISSION_CONFIG[member.permission];
                    const PermIcon = perm.icon;
                    const isLastFull = member.permission === 'full' && fullPermissionCount === 1;

                    return (
                      <div
                        key={member.org_member_id}
                        className="flex items-center justify-between py-2 px-1 rounded-md hover:bg-muted/30 group"
                      >
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={member.avatar_url || undefined} />
                            <AvatarFallback className="text-[10px] bg-muted">
                              {getInitials(member.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">
                              {member.full_name || member.email}
                            </p>
                            {member.full_name && (
                              <p className="text-[11px] text-muted-foreground truncate">
                                {member.email}
                              </p>
                            )}
                          </div>
                        </div>

                        {effectiveCanManage ? (
                          <div className="flex items-center gap-1">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                                >
                                  <PermIcon className={`h-3 w-3 ${perm.color}`} />
                                  <span>{perm.label}</span>
                                  <ChevronDown className="h-3 w-3 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-52 p-1.5" align="end">
                                <div className="space-y-0.5">
                                  {PERMISSION_LEVELS.map((p) => {
                                    const pc = PERMISSION_CONFIG[p];
                                    const Icon = pc.icon;
                                    const disabled = p !== 'full' && isLastFull;

                                    return (
                                      <button
                                        key={p}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-sm transition-colors ${
                                          disabled
                                            ? 'opacity-40 cursor-not-allowed'
                                            : 'hover:bg-muted/50'
                                        } ${member.permission === p ? 'bg-muted' : ''}`}
                                        onClick={() =>
                                          !disabled && handleUpdatePermission(member.org_member_id, p)
                                        }
                                        disabled={disabled}
                                      >
                                        <Icon className={`h-3.5 w-3.5 ${pc.color}`} />
                                        <span>{pc.label}</span>
                                      </button>
                                    );
                                  })}

                                  <div className="border-t border-border my-1" />

                                  <button
                                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-sm text-destructive hover:bg-destructive/10 transition-colors ${
                                      isLastFull ? 'opacity-40 cursor-not-allowed' : ''
                                    }`}
                                    onClick={() =>
                                      !isLastFull && handleRemoveMember(member.org_member_id)
                                    }
                                    disabled={isLastFull}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    <span>Retirer</span>
                                  </button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <PermIcon className={`h-3 w-3 ${perm.color}`} />
                            {perm.label}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {members.length === 0 && !loading && (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                      Aucun membre dans cet espace
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Make private button */}
          {effectiveCanManage && space.visibility === 'public' && (
            <div className="border-t border-border pt-3">
              <Button
                variant="ghost"
                className="w-full h-9 text-sm text-muted-foreground hover:text-foreground gap-2"
                onClick={() => {
                  toast.info("Utilisez les paramètres de l'espace pour changer la visibilité.");
                }}
              >
                <Lock className="h-4 w-4" />
                Rendre privé
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
